<template>
  <div>
    <layout-headers
      title="ratesPageTitle"
      text="ratesPageSubtitle"
      :redirectButtons="redirectButtons"
    />

    <filters
      :tableData="tableData"
      v-model="search"
      @filtered-list="getFilteredList"
      :fields="[['name', 'en'], 'meter_type', 'season_type', ['provider', 'legal_name'], ['customer_type', 'name']]"
    />

    <br>
    <el-card v-loading="!isLoaded">
      <div id="filters" style="margin-bottom: 15px; text-align: right">
        <el-checkbox
          v-if="!$route.params.id"
          class="filter-buttons"
          v-model="autoConfirm"
          border
        >{{ $t('ratesAutoconfirmButton') }}</el-checkbox>
        <el-button
          v-if="!$route.params.id"
          class="filter-buttons"
          type="primary"
          :disabled="selectedRates.length>0 ? false : true"
          @click="configureModal('modalConfirmTitle', 'modalConfirmText', 'confirmRates')"
        >{{ $t('ratesConfirmButton') }}</el-button>
        <el-button
          v-if="!$route.params.id"
          class="filter-buttons"
          type="primary"
          @click="publishRates()"
        >{{ $t('ratesPublishButton') }}</el-button>
        <el-dropdown class="filter-buttons" :hide-on-click="false" trigger="click">
          <el-button type="default">
            <i :class="filterLoading ? 'el-icon-loading' : 'el-icon-setting'"/>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item>
              <filter-by-i-d
                :label="$t('ratesProviderFilter')"
                :placeholder="$t('ratesProviderFilter')"
                optionLabel="legal_name"
                optionValue="provider"
                :defaultSearch="providerId ? providerId : null"
                :options="providers"
                :tableData="tableData"
                @filtered-list="getFilteredList"
              />
              <filter-by-i-d
                :label="$t('ratesDistributorFilter')"
                :placeholder="$t('ratesDistributorFilter')"
                optionLabel="legal_name"
                optionValue="distributor"
                :options="distributors"
                :tableData="tableData"
                @filtered-list="getFilteredList"
              />
              <filter-by-i-d
                :label="$t('ratesCustomerTypeFilter')"
                :placeholder="$t('ratesCustomerTypeFilter')"
                optionValue="customer_type"
                :options="customerTypes"
                :tableData="tableData"
                @filtered-list="getFilteredList"
              />
              <filter-by-options
                :label="$t('ratesMeterTypeFilter')"
                :placeholder="$t('ratesMeterTypeFilter')"
                optionValue="meter_type"
                :options="types.meterTypes"
                :tableData="tableData"
                @filtered-list="getFilteredList"
              />
              <filter-by-options
                :label="$t('ratesSeasonTypeFilter')"
                :placeholder="$t('ratesSeasonTypeFilter')"
                optionValue="season_type"
                :options="types.seasonTypes"
                :tableData="tableData"
                @filtered-list="getFilteredList"
              />
              <filter-by-options
                :label="$t('ratesPowerMeasurementFilter')"
                :placeholder="$t('ratesPowerMeasurementFilter')"
                optionValue="power_measurement"
                :options="types.powerMeasurementTypes"
                :tableData="tableData"
                @filtered-list="getFilteredList"
              />
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <el-table
        :empty-text="emptyText"
        :data="filteredData"
        :cell-style="headerCellStyle"
        :header-cell-style="headerCellStyle"
        @selection-change="handleSelectionChange"
        style="width: 100%"
        height="460"
      >
        <el-table-column v-if="ratesApiPath === 'fetchAllRates'" type="selection" width="55"/>

        <el-table-column v-if="ratesApiPath === 'fetchAllRates'" label="ID" prop="id" width="55"/>

        <el-table-column prop="confirmed" label="Confirmed" width="90">
          <div slot-scope="scope">
            <div v-if="scope.row.confirmed_at" class="green-box"></div>
            <div v-else class="red-box"></div>
          </div>
        </el-table-column>

        <el-table-column :label="$t('ratesName')">
          <div slot-scope="scope">
            <div>{{ scope.row.name ? scope.row.name[locale] : ''}}</div>
          </div>
        </el-table-column>

        <el-table-column prop="provider.legal_name" :label="$t('ratesProvider')"/>

        <el-table-column prop="distributor.legal_name" :label="$t('ratesDistributor')"/>

        <el-table-column :label="$t('ratesCustomerType')">
          <div slot-scope="scope">
            <div>{{ scope.row.customer_type.description[locale]}}</div>
          </div>
        </el-table-column>

        <el-table-column :label="$t('ratesMeterType')">
          <div slot-scope="scope">
            <div>{{ scope.row.meter_type === 'single_rate_meter' ? $t('meterTypeLabelSingle') : $t('meterTypeLabelDouble')}}</div>
          </div>
        </el-table-column>

        <el-table-column :label="$t('ratesSeasonType')">
          <div slot-scope="scope">
            <div v-if="scope.row.season_type === 'high'">{{ $t('seasonTypeLabelHigh')}}</div>
            <div v-else-if="scope.row.season_type === 'low'">{{ $t('seasonTypeLabelLow')}}</div>
            <div v-if="scope.row.season_type === 'no_season'">{{ $t('seasonTypeLabelNoSeason')}}</div>
          </div>
        </el-table-column>

        <el-table-column prop="power_measurement" :label="$t('ratesPowerMeasurement')">
          <div slot-scope="scope">
            <div v-if="scope.row.power_measurement === 'with'">{{ $t('powerTypeLabelWith')}}</div>
            <div
              v-else-if="scope.row.power_measurement === 'without'"
            >{{ $t('powerTypeLabelWithout')}}</div>
            <div
              v-if="scope.row.power_measurement === 'with_reactive'"
            >{{ $t('powerTypeLabelWithReactive')}}</div>
          </div>
        </el-table-column>

        <el-table-column :label="$t('ratesConsumptionType')">
          <div slot-scope="scope">
            <div>{{ scope.row.small_consumer ? $t('ratesSmallConsumerLabel') : $t('ratesBigConsumerLabel')}}</div>
          </div>
        </el-table-column>

        <el-table-column :label="$t('ratesActiveEnergy')">
          <el-table-column width="65" :label="$t('ratesHT')" sortable prop="active_energy_vt"/>
          <el-table-column width="65" :label="$t('ratesLT')" sortable prop="active_energy_mt"/>
        </el-table-column>

        <el-table-column :label="$t('ratesBillingCapacity')">
          <el-table-column width="65" :label="$t('ratesHT')" sortable prop="billing_capacity_vt"/>
          <el-table-column width="65" :label="$t('ratesLT')" sortable prop="billing_capacity_mt"/>
        </el-table-column>

        <el-table-column prop="energy_efficiency" sortable :label="$t('ratesEnergyEfficiency')"/>

        <el-table-column prop="metering_point" :label="$t('ratesMeteringPoint')"/>

        <el-table-column :label="$t('ratesReactiveEnergy')">
          <el-table-column width="65" :label="$t('ratesHT')" sortable prop="reactive_energy_vt"/>
          <el-table-column width="65" :label="$t('ratesLT')" sortable prop="reactive_energy_mt"/>
        </el-table-column>

        <el-table-column fixed="right" prop="actions" :label="$t('tableActions')" width="120">
          <template slot-scope="scope">
            <el-tooltip class="item" effect="dark" placement="left-start">
              <!-- <div slot="content">
                                {{ $t('ratesCreatedBy') }} {{ scope.row.created_by ? scope.row.created_by.name : '/'}} <br/>
                                {{ $t('ratesCreatedAt') }} {{ scope.row.created_at ? scope.row.created_at.date.slice(0,19) : '/' }} <br/>
                                {{ $t('ratesConfirmedBy') }} {{ scope.row.confirmed_by ? scope.row.confirmed_by.name : '/'}} <br/>
                                {{ $t('ratesConfirmedAt') }} {{scope.row.confirmed_at ? scope.row.confirmed_at.date.slice(0,19) : '/'}} <br/>
              </div>-->
              <el-button
                v-if="scope.row.confirmed_at"
                type="info"
                icon="el-icon-view"
                class="table-button"
                @click="$router.push({name: 'SingleRate', params: {id: scope.row.id}})"
              ></el-button>
              <el-button
                v-else
                type="primary"
                icon="el-icon-edit"
                class="table-button"
                @click="edit(scope.row.distributor ? 'SupplierRateEdit' : 'DistributorRateEdit', scope.row, 'rate')"
              ></el-button>
            </el-tooltip>

            <el-button
              type="danger"
              icon="el-icon-delete"
              class="table-button"
              @click="showModal(scope.row.id, scope.$index)"
            ></el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <modal :config="modalConfig" @discarded="closeModal" @closeModal="closeModal(after)"/>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  forbidden,
  success,
  warning
} from "@/components/Notification/notification.js";
import LayoutHeaders from "../LayoutPart/LayoutHeaders";
import Filters from "../Filters/Filter";
import FilterByID from "../Filters/FilterByID";
import FilterByOptions from "../Filters/FilterByOptions";
import { getFilteredList, edit } from "@/helpers/helpers";
import Modal from "../Modal/Modal";
import { showModal, closeModal } from "../Modal/ModalHelpers";
import Types from "@/model/dropdownTypes";
import Rate from "@/model/resources";
import api from "@/api/api";

export default {
  name: "rates",
  props: {
    providerId: {
      type: String
    },
    providerType: {
      type: String
    }
  },
  components: {
    Modal,
    LayoutHeaders,
    Filters,
    FilterByID,
    FilterByOptions
  },
  computed: {
    ...mapGetters({
      locale: "getLocale",
      rates: "getRates",
      getAutoConfirm: "getAutoConfirmation",
      providers: "getSuppliers",
      distributors: "getDistributors",
      customerTypes: "getCustomerTypes"
    })
  },
  data() {
    return {
      after: "deleteRate",
      isLoaded: false,
      filterLoading: false,
      autoConfirm: false,
      emptyText: "Loading...",
      ratesApiPath: "",
      modalConfig: { text: "", title: "", after: "", show: false },
      id: null,
      filteredData: [],
      search: "",
      selectedRates: [],
      tableData: [],
      types: Types,
      redirectButtons: [
        {
          path: "SupplierRateCreate",
          param: {
            isEdit: false,
            rate: {
              distributor_id: null,
              provider_id: this.$route.params.id
                ? Number(this.$route.params.id)
                : null,
              rate_type: "supplier"
            }
          },
          text: "ratesCreateSupplierRate"
        },
        {
          path: "DistributorRateCreate",
          param: {
            isEdit: false,
            rate: {
              distributor_id: null,
              provider_id: this.$route.params.id
                ? Number(this.$route.params.id)
                : null,
              rate_type: "distributor"
            }
          },
          text: "ratesCreateDistributorRate"
        }
      ]
    };
  },
  watch: {
    autoConfirm() {
      this.$store.dispatch("updateAutoConfirmation", this.autoConfirm);
    },
    rates() {
      this.tableData = this.rates;
      this.filteredData = this.rates;

      this.isLoaded = true;
    }
  },
  async created() {
    this.autoConfirm = this.getAutoConfirm;
    await this.getRatesApiPath(this.$route.params.id).then(res => {
      this.ratesApiPath = res;
      this.emptyText = "No results";
    });
    let buttons = [];
    if (this.providerType === "supplier") {
      buttons.push(this.redirectButtons[0]);
      this.redirectButtons = buttons;
    } else if (this.providerType === "distributor") {
      buttons.push(this.redirectButtons[1]);
      this.redirectButtons = buttons;
    }
    await this.$store.dispatch("fetchSuppliers");
    await this.$store.dispatch("fetchDistributors");
    await this.$store.dispatch("fetchCustomerTypes");
  },
  methods: {
    edit,
    forbidden,
    getFilteredList,
    warning,
    success,
    showModal,
    closeModal,
    configureModal(title, text, after) {
      this.modalConfig.title = title;
      this.modalConfig.text = text;
      this.after = after;
      this.showModal();
    },
    confirmRates() {
      if (this.selectedRates.length) {
        this.$store.dispatch("confirmRates", this.selectedRates).then(res => {
          this.$store.dispatch(this.ratesApiPath);
        });
      } else {
        this.warning("You need to select at least one rate");
      }
    },
    async publishRates() {
      await api.publishRates().then(res => {
        if (res.status === 200) {
          this.success("Rates", "published.");
        }
      });
    },
    async getRatesApiPath(provider) {
      let path = "";
      let id = null;
      let user;
      await api.fetchSelf().then(res => {
        user = res.data.data;
      });
      if (user.roles[0].name === "provider") {
        path = "fetchRates";
        id = user.provider ? user.provider.id : null;
      } else if (provider) {
        path = "fetchProviderRates";
        id = provider;
      } else {
        path = "fetchAllRates";
      }
      this.$store.dispatch(path, id);
      return path;
    },
    headerCellStyle({ row, column, rowIndex, columnIndex }) {
      return {
        wordBreak: `normal`,
        textAlign: `center`,
        wordWrap: `normal`
      };
    },
    deleteRate() {
      this.modalConfig.show = false;

      if (this.id) {
        this.$store.dispatch("removeRate", this.id);
      } else {
        this.showMessage("Id is null");
      }
    },
    handleSelectionChange(data) {
      this.selectedRates = [];
      for (let item of data) {
        this.selectedRates.push(item.id);
      }
    }
  }
};
</script>

<style scoped>
.table-button {
  width: 30px !important;
  height: 30px !important;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  color: darkgrey;
  font-size: 20px;
  font-weight: 800;
  border: none;
}

.table-button:hover {
  color: #409eff !important;
}

.el-dropdown-menu__item:hover {
  background-color: #fff;
  color: black;
  height: 100%;
}

.filter-buttons {
  display: inline-block;
}
.el-table thead.is-group th {
  background: none !important;
}
.el-table .cell .table-links {
  font-weight: bold;
  color: #89bef3;
  cursor: pointer;
}

.green-box {
  width: 20px;
  height: 20px;
  background: green;
  display: block;
  margin: 0 auto;
}

.red-box {
  width: 20px;
  height: 20px;
  background: red;
  display: block;
  margin: 0 auto;
}

.loading-icon {
  font-size: 40px;
  color: #89bef3;
  margin-top: 40px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Rates/Rates.vue